import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Work,
  CheckCircle,
  Timeline,
  Assignment,
  AttachMoney,
  BusinessCenter,
  AccountBalance,
  EmojiEvents,
} from '@mui/icons-material';

const visaTypes = [
  {
    title: 'Skilled Worker Visa',
    description: 'For qualified workers with a job offer from a UK employer',
    requirements: ['Job offer from licensed sponsor', 'Skill level RQF3 or above', 'English language requirement'],
  },
  {
    title: 'Health and Care Worker Visa',
    description: 'For medical professionals and care workers',
    requirements: ['Job offer in healthcare sector', 'Qualification verification', 'English language requirement'],
  },
  {
    title: 'Global Talent Visa',
    description: 'For leaders or potential leaders in academia, research, arts, or digital technology',
    requirements: ['Endorsement from approved body', 'Evidence of achievements', 'Research/work portfolio'],
  },
  {
    title: 'Intra-company Transfer',
    description: 'For employees transferring to UK branch of their company',
    requirements: ['Existing employer relationship', 'Minimum salary requirement', 'Skill level RQF6 or above'],
  },
];

const benefits = [
  'Access to UK job market',
  'Path to settlement',
  'Bring family members',
  'NHS healthcare access',
  'Career progression opportunities',
  'Global work experience',
];

const process = [
  {
    title: 'Eligibility Assessment',
    description: 'Evaluate your qualifications and work experience',
  },
  {
    title: 'Sponsor License',
    description: 'Guidance on employer sponsorship requirements',
  },
  {
    title: 'Document Preparation',
    description: 'Assistance with gathering required documentation',
  },
  {
    title: 'Application Filing',
    description: 'Professional support with visa application',
  },
  {
    title: 'Post-Application Support',
    description: 'Guidance on next steps and settling in the UK',
  },
];

const WorkVisa = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Work sx={{ fontSize: 64, mb: 3, color: theme.palette.secondary.light }} />
              <Typography variant="h1" gutterBottom>
                UK Work Visa Services
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                Your pathway to professional success in the United Kingdom
              </Typography>
              <Button
                component={RouterLink}
                to="/consultation"
                variant="contained"
                color="secondary"
                size="large"
                sx={{ mr: 2 }}
              >
                Free Consultation
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Overview Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom>
              Work in the UK
            </Typography>
            <Typography variant="body1" paragraph>
              The UK offers various work visa routes for skilled professionals looking to advance their careers. Our expert team provides comprehensive support to help you navigate the UK's points-based immigration system and secure the right work visa for your circumstances.
            </Typography>
            <Typography variant="body1">
              Whether you're a skilled worker, healthcare professional, or business leader, we'll guide you through every step of the visa application process.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  Key Benefits
                </Typography>
                <List>
                  {benefits.map((benefit) => (
                    <ListItem key={benefit}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Visa Types Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Types of Work Visas
          </Typography>
          <Grid container spacing={4}>
            {visaTypes.map((visa) => (
              <Grid item xs={12} md={6} key={visa.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                      {visa.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {visa.description}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      Key Requirements:
                    </Typography>
                    <List>
                      {visa.requirements.map((req) => (
                        <ListItem key={req}>
                          <ListItemIcon>
                            <Assignment color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={req} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Application Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Application Process
          </Typography>
          <Grid container spacing={3}>
            {process.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Timeline color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Step {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose Us */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Why Choose Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <EmojiEvents color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Expert Guidance
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    Our team of experienced immigration lawyers specializes in UK work visas. We stay updated with the latest immigration rules and policies to provide you with accurate advice and support.
                  </Typography>
                  <Typography variant="body1">
                    We have helped numerous professionals successfully obtain their UK work visas and establish their careers in the United Kingdom.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <BusinessCenter color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Comprehensive Support
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    We provide end-to-end support throughout your visa application process, from initial assessment to post-arrival guidance.
                  </Typography>
                  <Typography variant="body1">
                    Our services include sponsor license assistance, document preparation, application review, and ongoing support to ensure your visa application success.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            py: 8,
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: 3,
          }}
        >
          <Typography variant="h3" gutterBottom>
            Ready to Start Your UK Career Journey?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Contact us today for a free consultation and expert guidance
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ px: 6, py: 2 }}
          >
            Book Free Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default WorkVisa; 